import type {
  InsightBarChartElement,
  InsightChartContent,
  InsightColumnDefinition,
  InsightElement,
  InsightFunnelElement,
  InsightLineGraphContent,
  InsightLineGraphElement,
  InsightLinePoint,
  InsightLineSeries,
} from '../models/insight.types'

import {
  columnsFromRows,
  resolveInsightColumns,
  type InsightColumnAccessors,
} from './insightColumns'
import { isInsightRecord } from './insightValue'

import {
  buildChartOptions,
  type ChartSpec,
  type ChartType,
  type ReportColumn,
  type ReportRow,
} from '@/features/reports'

export interface InsightTableMapping {
  kind: 'table'
  rowCount: number | null
  columns: ReportColumn[]
  rows: ReportRow[]
  total: Record<string, unknown> | null
  fallbackFrom?: InsightElement['element_type']
}

export interface InsightChartMapping {
  kind: 'chart'
  spec: ChartSpec
}

export interface InsightRawMapping {
  kind: 'raw'
  payload: unknown
}

export type InsightElementMapping = InsightTableMapping | InsightChartMapping | InsightRawMapping

export interface InsightElementMapperAccessors extends InsightColumnAccessors {
  /**
   * The insight config's own `column_definitions`. v3 merges these ahead of the element's
   * (`InsightTable.vue:getColumnDefinitions`), which is what decides where a column shared by
   * both lands -- `suggested_action` sits 3rd in a placement config but last on the element.
   */
  configColumns?: readonly InsightColumnDefinition[]
  /** Table rows are the data source for funnel elements in the published Insights wire shape. */
  siblingRows?: readonly ReportRow[]
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function normalizeCell(value: unknown): unknown {
  if (value == null || value === '') return null
  if (typeof value === 'number' && !Number.isFinite(value)) return null
  return value
}

function rowsFrom(value: unknown): ReportRow[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((row) => {
    const record = isInsightRecord(row) ? row : null
    if (!record) return []
    return [
      Object.fromEntries(Object.entries(record).map(([key, cell]) => [key, normalizeCell(cell)])),
    ]
  })
}

export function elementToTable(
  element: InsightElement,
  accessors: InsightElementMapperAccessors = {},
): InsightTableMapping | null {
  if (element.element_type !== 'table' || !Array.isArray(element.content)) return null
  const rows = rowsFrom(element.content)
  if (rows.length !== element.content.length) return null
  const metadataHidden = Array.isArray(element.metadata['hidden_dimensions'])
    ? element.metadata['hidden_dimensions']
    : []
  const hidden = new Set(
    metadataHidden.filter((value): value is string => typeof value === 'string'),
  )
  const frozen = new Set(element.frozen_columns)
  const columns = resolveInsightColumns(
    accessors.configColumns ?? [],
    element.column_definitions ?? [],
    frozen,
    rows,
    hidden,
    accessors,
  )
  return {
    kind: 'table',
    columns,
    rows,
    total: element.totals,
    rowCount: typeof element.row_count === 'number' ? element.row_count : null,
  }
}

function chartRows(content: InsightChartContent): ReportRow[] {
  return rowsFrom(content.data)
}

function metricLabelFrom(
  metadata: Record<string, unknown>,
  accessors: InsightElementMapperAccessors,
): (field: string) => string {
  const title = stringValue(metadata['title']) ?? stringValue(metadata['chart_title'])
  return (field) => title ?? accessors.metricLabel?.(field) ?? field
}

function categoricalSpec(
  type: ChartType,
  rows: ReportRow[],
  dimension: string,
  metric: string,
  metadata: Record<string, unknown>,
  accessors: InsightElementMapperAccessors,
): ChartSpec {
  return {
    type,
    rows,
    dimensions: [dimension],
    metrics: [metric],
    metricLabel: metricLabelFrom(metadata, accessors),
  }
}

function barChartSpec(
  element: InsightBarChartElement,
  accessors: InsightElementMapperAccessors,
): ChartSpec | null {
  const dimension = stringValue(element.metadata['dimension'])
  const metric = stringValue(element.metadata['metric'])
  if (!dimension || !metric) return null
  const rows = chartRows(element.content)
  const limit = Number(element.metadata['limit'])
  return categoricalSpec(
    'bar',
    Number.isFinite(limit) && limit > 0 ? rows.slice(0, limit) : rows.slice(0, 10),
    dimension,
    metric,
    element.metadata,
    accessors,
  )
}

function isLineSeries(value: unknown): value is InsightLineSeries {
  const record = isInsightRecord(value) ? value : null
  return !!record && Array.isArray(record['points'])
}

function linePointRow(point: InsightLinePoint, xField: string, yField: string): ReportRow {
  return { ...point, [xField]: point.x, [yField]: point.y }
}

function activeSeries(content: InsightLineGraphContent): InsightLineSeries | undefined {
  const series = (content.data as unknown[]).filter(isLineSeries)
  return (
    series.find((item) => String(item.entity_id) === String(content.default_entity_id)) ?? series[0]
  )
}

function lineChartSpec(
  element: InsightLineGraphElement,
  accessors: InsightElementMapperAccessors,
): ChartSpec | null {
  const xField = stringValue(element.metadata['x_field'])
  const yField = stringValue(element.metadata['y_field'])
  if (!xField || !yField || !Array.isArray(element.content.data)) return null
  const active = activeSeries(element.content)
  if (!active) return null
  const rows = active.points.map((point) => linePointRow(point, xField, yField))
  return categoricalSpec('line', rows, xField, yField, element.metadata, accessors)
}

function funnelChartSpec(
  element: InsightFunnelElement,
  accessors: InsightElementMapperAccessors,
): ChartSpec | null {
  const config = isInsightRecord(element.content.config) ? element.content.config : {}
  const dimension =
    stringValue(element.metadata['dimension']) ??
    stringValue(config['dimension']) ??
    stringValue(config['stage_field']) ??
    stringValue(config['stage'])
  const metric =
    stringValue(element.metadata['metric']) ??
    stringValue(config['metric']) ??
    stringValue(config['value_field']) ??
    stringValue(config['value'])
  const rows = accessors.siblingRows ?? []
  const inferredDimension = rows.length > 0 ? Object.keys(rows[0] ?? {})[0] : undefined
  const inferredMetric =
    rows.length > 0
      ? Object.keys(rows[0] ?? {}).find((field) => typeof rows[0]?.[field] === 'number')
      : undefined
  const resolvedDimension = dimension ?? inferredDimension
  const resolvedMetric = metric ?? inferredMetric
  if (!resolvedDimension || !resolvedMetric || rows.length === 0) return null
  return categoricalSpec(
    'funnel',
    [...rows],
    resolvedDimension,
    resolvedMetric,
    { ...config, ...element.metadata },
    accessors,
  )
}

/** Production-backed element-only mapper fixed by ORP-18734 / BE-INS-03. */
export function elementToChartSpec(
  element: InsightElement,
  accessors: InsightElementMapperAccessors = {},
): ChartSpec | null {
  // A chart element whose content arrived as an array contradicts the contract; stay tolerant.
  if (Array.isArray(element.content)) return null
  if (element.element_type === 'bar_chart') {
    return barChartSpec(element, accessors)
  }
  if (element.element_type === 'line_graph') {
    return lineChartSpec(element, accessors)
  }
  if (element.element_type === 'funnel') {
    return funnelChartSpec(element, accessors)
  }
  return null
}

function lineFallbackRows(element: InsightLineGraphElement): ReportRow[] {
  const active = activeSeries(element.content)
  if (!active) return []
  const xField = stringValue(element.metadata['x_field']) ?? 'x'
  const yField = stringValue(element.metadata['y_field']) ?? 'y'
  return active.points.map((point) => linePointRow(point, xField, yField))
}

function fallbackRows(element: InsightElement): ReportRow[] {
  if (Array.isArray(element.content) || !Array.isArray(element.content.data)) return []
  if (element.element_type === 'line_graph') return lineFallbackRows(element)
  return rowsFrom(element.content.data)
}

/** A valid chart row container with incomplete hints remains readable through module 08's table. */
export function elementToFallbackTable(
  element: InsightElement,
  accessors: InsightElementMapperAccessors = {},
): InsightTableMapping | null {
  if (element.element_type === 'table') return null
  const rows = fallbackRows(element)
  if (rows.length === 0) return null
  return {
    kind: 'table',
    rowCount: null,
    columns: columnsFromRows(rows, accessors),
    rows,
    total: element.totals,
    fallbackFrom: element.element_type,
  }
}

export function mapInsightElement(
  element: InsightElement,
  accessors: InsightElementMapperAccessors = {},
  siblingRows?: readonly ReportRow[],
): InsightElementMapping {
  const table = elementToTable(element, accessors)
  if (table) return table
  const resolvedAccessors = siblingRows ?? accessors.siblingRows
  const spec = elementToChartSpec(
    element,
    resolvedAccessors === undefined ? accessors : { ...accessors, siblingRows: resolvedAccessors },
  )
  if (spec && buildChartOptions(spec) !== null) return { kind: 'chart', spec }
  const fallback = elementToFallbackTable(element, accessors)
  if (fallback) return fallback
  return { kind: 'raw', payload: element.content }
}
